<template>
  <transition name="checklist-fade">
    <div
      v-if="visible"
      ref="root"
      class="checklist-modal"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
      @mousedown.self="onClose"
    >
      <div class="checklist-modal__panel" role="document" @mousedown.stop>
        <header class="checklist-modal__head">
          <span class="checklist-modal__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path class="checklist-modal__icon-board" d="M9 2h6a1 1 0 0 1 1 1v1h2a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2V3a1 1 0 0 1 1-1z" />
              <path class="checklist-modal__icon-tick" d="M9 13l2 2 4-4" />
            </svg>
          </span>
          <div class="checklist-modal__heading">
            <h3 :id="titleId">{{ title }}</h3>
            <p v-if="subtitle">{{ subtitle }}</p>
          </div>
          <button type="button" class="checklist-modal__close" aria-label="Close" @click="onClose">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <div class="checklist-modal__progress" aria-hidden="true">
          <div class="checklist-modal__progress-bar" :style="{ width: progressPct + '%' }"></div>
        </div>

        <div class="checklist-modal__body">
          <ul class="checklist-modal__list">
            <li
              v-for="(item, index) in orderedItems"
              :key="item.key"
              :class="['checklist-row', item.ok ? 'checklist-row--ok' : 'checklist-row--todo']"
              :style="{ transitionDelay: (index * 45) + 'ms' }"
            >
              <span class="checklist-row__mark" aria-hidden="true">
                <svg v-if="item.ok" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <svg v-else viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="12" y1="8" x2="12" y2="13" />
                  <line x1="12" y1="16.5" x2="12.01" y2="16.5" />
                </svg>
              </span>
              <span class="checklist-row__text">
                <span class="checklist-row__label">{{ item.label }}</span>
                <span v-if="!item.ok && item.hint" class="checklist-row__hint">{{ item.hint }}</span>
              </span>
              <button
                v-if="!item.ok && item.target"
                type="button"
                class="checklist-row__locate"
                @click="onLocate(item)"
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11z" />
                  <circle cx="12" cy="10" r="2.5" />
                </svg>
                <span>Take me there</span>
              </button>
            </li>
          </ul>
        </div>

        <footer class="checklist-modal__foot">
          <button type="button" class="checklist-modal__btn" @click="onClose">
            {{ actionLabel }}
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
let checklistIdCounter = 0

export default {
  name: 'FormChecklistModal',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    actionLabel: { type: String, default: 'Got it — let me fix these' }
  },
  data () {
    checklistIdCounter += 1
    return {
      titleId: `checklist-modal-title-${checklistIdCounter}`
    }
  },
  computed: {
    // Pending items float to the top so the agent reads "what's left"
    // first; completed ones sit below as reassurance / progress.
    orderedItems () {
      const todo = this.items.filter((i) => !i.ok)
      const done = this.items.filter((i) => i.ok)
      return [...todo, ...done]
    },
    readyCount () {
      return this.items.filter((i) => i.ok).length
    },
    progressPct () {
      if (!this.items.length) return 0
      return Math.round((this.readyCount / this.items.length) * 100)
    }
  },
  watch: {
    visible (now) {
      if (now) {
        this.$nextTick(() => {
          this.attachPanel()
          this.lockBodyScroll()
          if (this.$refs.root) this.$refs.root.focus()
        })
        document.addEventListener('keydown', this.onKeydown)
      } else {
        document.removeEventListener('keydown', this.onKeydown)
        this.unlockBodyScroll()
      }
    }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
    this.unlockBodyScroll()
    this.detachPanel()
  },
  methods: {
    attachPanel () {
      const root = this.$refs.root
      if (root && root.parentNode !== document.body) {
        document.body.appendChild(root)
      }
    },
    detachPanel () {
      const root = this.$refs.root
      if (root && root.parentNode === document.body) {
        document.body.removeChild(root)
      }
    },
    lockBodyScroll () {
      this._prevBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    },
    unlockBodyScroll () {
      if (this._prevBodyOverflow !== undefined) {
        document.body.style.overflow = this._prevBodyOverflow
        this._prevBodyOverflow = undefined
      }
    },
    onKeydown (event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.onClose()
      }
    },
    onClose () {
      this.$emit('close')
    },
    // Let the parent close, scroll to and highlight the offending field.
    onLocate (item) {
      this.$emit('locate', item)
    }
  }
}
</script>

<style scoped>
.checklist-modal {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  outline: none;
}

.checklist-modal__panel {
  width: 100%;
  max-width: 460px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.28), 0 2px 6px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.checklist-modal__head {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1.1rem 1.15rem 0.75rem;
}

.checklist-modal__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 11px;
  flex-shrink: 0;
  background: #fef3c7;
  color: #b45309;
}

.checklist-modal__icon-board {
  stroke-dasharray: 80;
  stroke-dashoffset: 80;
  animation: checklist-draw 0.5s ease forwards 0.1s;
}

.checklist-modal__icon-tick {
  stroke-dasharray: 14;
  stroke-dashoffset: 14;
  animation: checklist-draw 0.35s ease forwards 0.5s;
}

@keyframes checklist-draw {
  to { stroke-dashoffset: 0; }
}

.checklist-modal__heading {
  flex: 1;
  min-width: 0;
  padding-top: 0.1rem;
}

.checklist-modal__heading h3 {
  margin: 0 0 0.2rem;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.25;
}

.checklist-modal__heading p {
  margin: 0;
  font-size: 0.82rem;
  color: #4b5563;
  line-height: 1.45;
}

.checklist-modal__close {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.checklist-modal__close:hover { background: #f1f5f9; color: #475569; }
.checklist-modal__close:focus { outline: none; }
.checklist-modal__close::-moz-focus-inner { border: 0; }
.checklist-modal__close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.checklist-modal__progress {
  height: 4px;
  background: #eef1f7;
  overflow: hidden;
}

.checklist-modal__progress-bar {
  height: 100%;
  border-radius: 0 4px 4px 0;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  transition: width 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}

.checklist-modal__body {
  padding: 0.65rem 1.15rem 0.4rem;
  max-height: min(52vh, 420px);
  overflow-y: auto;
}

.checklist-modal__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.checklist-row {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.6rem 0.7rem;
  border-radius: 10px;
  border: 1px solid #eef1f7;
  background: #fbfcfe;
  /* Staggered reveal — delay is applied inline per row index. */
  animation: checklist-row-in 0.32s ease both;
}

@keyframes checklist-row-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

.checklist-row--todo {
  border-color: #fde2c4;
  background: #fffaf2;
}

.checklist-row__mark {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  margin-top: 0.05rem;
}

.checklist-row--ok .checklist-row__mark { background: #dff5ea; color: #0e5b46; }
.checklist-row--todo .checklist-row__mark { background: #fde9d2; color: #b45309; }

.checklist-row__text {
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
  min-width: 0;
  flex: 1;
}

.checklist-row__locate {
  flex-shrink: 0;
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 28px;
  padding: 0 0.6rem;
  border-radius: 7px;
  border: 1px solid #fcd9a8;
  background: #fff;
  color: #b45309;
  font-family: inherit;
  font-size: 0.74rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.checklist-row__locate:hover {
  background: #fff7ed;
  border-color: #f59e0b;
  color: #92531a;
  transform: translateY(-1px);
}

.checklist-row__locate:active { transform: translateY(0); }
.checklist-row__locate:focus { outline: none; }
.checklist-row__locate::-moz-focus-inner { border: 0; }
.checklist-row__locate:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.3);
}

.checklist-row__label {
  font-size: 0.86rem;
  font-weight: 600;
  color: #1f2a44;
  line-height: 1.3;
}

.checklist-row--ok .checklist-row__label { color: #4b5563; }

.checklist-row__hint {
  font-size: 0.78rem;
  color: #92531a;
  line-height: 1.4;
}

.checklist-modal__foot {
  display: flex;
  justify-content: flex-end;
  padding: 0.75rem 1.15rem 1rem;
}

.checklist-modal__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 1.15rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
  cursor: pointer;
  color: #ffffff;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  font-family: inherit;
}

.checklist-modal__btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.3);
  filter: brightness(1.05);
}

.checklist-modal__btn:active { transform: translateY(0); filter: brightness(0.96); }
.checklist-modal__btn:focus { outline: none; }
.checklist-modal__btn::-moz-focus-inner { border: 0; }
.checklist-modal__btn:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.checklist-fade-enter-active,
.checklist-fade-leave-active {
  transition: opacity 0.18s ease;
}

.checklist-fade-enter-active .checklist-modal__panel,
.checklist-fade-leave-active .checklist-modal__panel {
  transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}

.checklist-fade-enter,
.checklist-fade-leave-to {
  opacity: 0;
}

.checklist-fade-enter .checklist-modal__panel,
.checklist-fade-leave-to .checklist-modal__panel {
  transform: translateY(14px) scale(0.97);
}

@media (max-width: 480px) {
  .checklist-modal {
    padding: 0.75rem;
    align-items: flex-end;
  }

  .checklist-modal__panel { max-width: 100%; }

  .checklist-modal__foot { padding-bottom: 1.25rem; }

  .checklist-modal__btn { width: 100%; }
}
</style>
